import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as SearchMagnifyIcon } from '../../assets/svg/SearchMagnifyIcon.svg';
import Loading from '../Loading';
import FilterCourses from '../courses/FilterCourses';
import { getLocales, getSolutionCategories, getSolutionTopics } from '../../server/server';
import { fileSystemUrl } from '../../server/connections';
import SelectStringComponent from '../SelectStringComponent';
import BigImageModal from '../modals/BigImageModal';
import BulkImportModal from '../modals/BulkImportModal';
import InfiniteScroll from 'react-infinite-scroll-component';

function SolutionsHome() {
	const navigate = useNavigate();
	const [currentPath, setCurrentPath] = useState(window.location.pathname);
	const [isParent, setIsParent] = useState(window.location.pathname.includes('parent'));
	const [solutionTopics, setSolutionTopics] = useState<any>([]);
	const [loading, setLoading] = useState(true);
	const [loadingCategories, setLoadingCategories] = useState(true);
	const [filterState, setFilterState] = useState('');
	const [categories, setCategories] = useState<any>([]);
	const [locales, setLocales] = useState<any>([]);
	const [localesName, setLocalesName] = useState<string[]>([]);
	const [locale, setLocale] = useState('English');
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');
	const [search, setSearch] = useState('');
	const [loadingMore, setLoadingMore] = useState(false);
	const timeout = useRef<any>(null);
	const [resultsNum, setResultsNum] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const isFirstMount = useRef(true);
	const [bulkImportModalOpen, setBulkImportModalOpen] = useState(false);

	useEffect(() => {
		loadCategories();
		loadLocales();
	}, []);

	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false;
			return;
		}
		if (resultsNum === 0) loadSolutions();
		else setResultsNum(0);
	}, [filterState, locale, currentPath]);

	useEffect(() => {
		console.log('resultsNum ', resultsNum);
		loadSolutions();
	}, [resultsNum]);

	useEffect(() => {
		handleUrlChange();
	}, [window.location.pathname]);

	const loadSolutions = async (searchTerm: string = search) => {
		if (resultsNum === 0) setLoading(true);
		else setLoadingMore(true);
		const resp = await getSolutionTopics({ limit: 10, offset: resultsNum, title: searchTerm, category: filterState, locale: filterLocale() });
		if (resp.status == 200) {
			const data = resp.data?.items;
			const total = resp.data?.total;
			if (resultsNum === 0) {
				setSolutionTopics(data);
				setHasMore(total > data?.length);
			} else {
				setHasMore(total > solutionTopics.length + data?.length);
				setSolutionTopics((prev: any) => [...prev, ...data]);
			}

			setLoadingMore(false);
			setLoading(false);
		} else {
			setLoading(false);
		}
	};

	const handleUrlChange = () => {
		setCurrentPath(window.location.pathname);
		setIsParent(window.location.pathname.includes('parent'));
	};

	const handleChange = (e: any) => {
		e.preventDefault();
		setSearch(e.target.value);
		if (timeout !== null) {
			clearTimeout(timeout.current);
		}
		timeout.current = setTimeout(() => {
			if (resultsNum === 0) loadSolutions(e.target.value);
			else setResultsNum(0);
		}, 700);
	};

	const filterLocale = () => {
		const filterName: any[] = locales.filter((item: any) => locale === item.name);
		if (filterName.length !== 0) return filterName[0].code ?? '';
		else {
			return 'EN';
		}
	};

	const loadCategories = async () => {
		const resp = await getSolutionCategories(isParent ? 'PARENT' : 'CHILD');
		if (resp.status === 200) {
			setCategories([...resp.data]);
			setLoadingCategories(false);
		} else {
			setLoadingCategories(false);
		}
	};

	const loadLocales = async () => {
		const resp = await getLocales();
		if (resp.status === 200) {
			setLocales(resp.data);
			let list = resp.data.map((item: any) => {
				return item.name;
			});
			setLocalesName(list);
		}
	};

	const openSolution = (solution: any) => {
		if (isParent) navigate(`/parent-solutions/${solution?.id}`);
		else navigate(`/solutions/${solution?.id}`);
	};

	const openNewSolution = () => {
		if (isParent) navigate('/parent-solutions/new');
		else navigate('/solutions/new');
	};

	const openImageModal = (image: string) => {
		setImageModalOpen(true);
		setSelectedImage(image);
	};

	const closeImageModal = () => {
		setImageModalOpen(false);
		setSelectedImage('');
	};

	const fetchAllSolutionTopics = async (): Promise<any[]> => {
		const resp = await getSolutionTopics({ limit: 1000, offset: 0, title: '', category: '', locale: filterLocale() });
		if (resp.status === 200) {
			return resp.data?.items || [];
		}
		return [];
	};

	const handleBulkImport = (selectedIds: string[]) => {
		console.log('Selected solution topic IDs:', selectedIds);
		// You can implement what to do with the selected IDs here
		// For example, navigate to a page with these IDs, or trigger an action
		alert(`Imported ${selectedIds.length} solution topics:\n${selectedIds.join('\n')}`);
	};

	return (
		<div id='scroll-inf-task' className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className=' w-full min-h-fit   max-w-[1200px] px-[10px]'>
				<div className='w-full min-h-[calc(100vh-80px)]  bg-white flex flex-col px-[40px] py-[30px] rounded-[10px]'>
					<div className='mb-[30px] flex flex-row  items-end gap-4'>
						<p className='flex-1 text-[40px] leading-[50px] font-bold text-darkViolet'>Solution topics</p>
						<SelectStringComponent value={locale} selectedOption='English' setValue={setLocale} width={140} height={50} options={localesName} />
						<div
							className={`cursor-pointer flex h-[50px]  min-w-[180px] px-3 items-center justify-center rounded-full border border-mudPurple
									bg-white text-[18px] leading-6  font-semibold text-mudPurple transition  hover:bg-mudPurple hover:text-white`}
							onClick={() => setBulkImportModalOpen(true)}
						>
							Bulk Import
						</div>
						<div
							className={`cursor-pointer flex h-[50px]  min-w-[180px] px-3 items-center justify-center rounded-full border border-primaryViolet
									bg-white text-[18px] leading-6  font-semibold text-primaryViolet transition  hover:bg-primaryViolet hover:text-brandYellow`}
							onClick={() => openNewSolution()}
						>
							Add new topic
						</div>
					</div>
					<div className=' flex w-full flex-row gap-5'>
						<div className='flex  h-[50px] flex-1 flex-row items-center rounded-full border border-primaryViolet  px-4'>
							<input type='text' placeholder='Search' value={search} onChange={(e: any) => handleChange(e)} className='h-full flex-1 rounded-full  outline-none  text-[18px] leading-6  font-semibold text-primaryViolet placeholder:font-medium placeholder:text-mudPurple' autoComplete='off' />
							<SearchMagnifyIcon />
						</div>
					</div>
					{!loadingCategories && <FilterCourses path={isParent ? '/parent-solution-categories' : '/solution-categories'} filterState={filterState} setFilterState={setFilterState} categories={categories} />}

					{solutionTopics?.length === 0 && !loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							{' '}
							<p className='text-center text-[14px] font-medium leading-5 text-mudPurple'>
								This list is empty right now.
								<br /> Consider adding new solution topic...
							</p>{' '}
						</div>
					)}
					{!loading && (
						<div>
							<InfiniteScroll
								dataLength={solutionTopics.length}
								next={() => {
									setResultsNum(solutionTopics.length);
								}}
								hasMore={hasMore}
								scrollableTarget='scroll-inf-task'
								inverse={false}
								loader={undefined}
							>
								{solutionTopics.map((item: any, index: number) => (
									<SolutionButton solution={item} openImageModal={openImageModal} openSolution={openSolution} key={item.id} />
								))}
							</InfiniteScroll>
						</div>
					)}
					{(loading || loadingMore) && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}
				</div>
			</div>
			<BigImageModal modalIsOpen={imageModalOpen} closeModal={closeImageModal} selectedImage={selectedImage} />
		</div>
	);
}

export default SolutionsHome;

const SolutionButton = ({ solution, openSolution, openImageModal }: { solution: any; openSolution: any; openImageModal: any }) => {
	return (
		<div
			className='my-[5px] flex cursor-pointer flex-row gap-5 rounded-[10px] border border-[#F8F8FB] bg-[#F8F8FB] px-[40px] py-[25px] duration-300 ease-out hover:border-mudPurple'
			key={solution?.id}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				openSolution(solution);
			}}
		>
			{!!solution.image && (
				<div
					className='w-full max-w-[100px] shrink-0'
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						openImageModal(fileSystemUrl + '/' + solution.image?.url);
					}}
				>
					<img className='h-[50px] w-full rounded-xl object-contain' src={fileSystemUrl + '/' + solution.image?.url} alt={solution?.title || 'Solution image'} />
				</div>
			)}
			<div className='flex flex-1 flex-col justify-center'>
				<h3 className='text-[20px] font-semibold leading-7 text-primaryViolet'>{solution?.title}</h3>
				<p className='two-lines-of-text mt-[8px] text-[14px] font-medium leading-5 text-mudPurple'>{solution?.description}</p>
			</div>
			<div className='flex w-[300px] shrink-0 flex-col justify-center gap-2'>
				<div className='flex flex-row items-center'>
					<span className='text-[16px] font-semibold leading-6 text-primaryViolet'>Locale:</span>
					<span className='ml-2 truncate text-[16px] font-medium leading-6 text-primaryViolet'>{solution?.locale?.name || 'N/A'}</span>
				</div>
				<div className='flex flex-row items-center'>
					<span className='whitespace-nowrap text-[16px] font-semibold leading-6 text-primaryViolet'>Category:</span>
					<span className='ml-2 truncate text-[16px] font-medium leading-6 text-primaryViolet'>{solution?.category?.name || 'N/A'}</span>
				</div>
			</div>
		</div>
	);
};
